/**
 * Sidebar State Management
 *
 * Pure functions for managing sidebar state, including localStorage
 * persistence for sidebar expansion state and dropdown open/close states.
 */

import { STORAGE_KEYS } from "./config";

// ============================================
// Safe localStorage Utilities
// ============================================

/**
 * Safely gets an item from localStorage.
 * Returns null on any error (SecurityError in private browsing, etc.).
 */
function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.debug(`[sidebar] localStorage.getItem failed for "${key}":`, error);
    return null;
  }
}

/**
 * Safely sets an item in localStorage.
 * Silently fails on any error (SecurityError, QuotaExceededError, etc.).
 */
function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.debug(`[sidebar] localStorage.setItem failed for "${key}":`, error);
  }
}

/**
 * Safely removes an item from localStorage.
 */
function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.debug(`[sidebar] localStorage.removeItem failed for "${key}":`, error);
  }
}

// ============================================
// Sidebar Expansion State
// ============================================

/**
 * Checks if the sidebar toggle button indicates expanded state.
 *
 * Args:
 *   toggleEl: The toggle button element, or null.
 *
 * Returns:
 *   boolean: True if aria-expanded is "true", false otherwise.
 */
export function isSidebarExpanded(toggleEl: HTMLElement | null): boolean {
  if (!toggleEl) return false;
  return toggleEl.getAttribute("aria-expanded") === "true";
}

/**
 * Gets the saved sidebar expansion state from localStorage.
 *
 * Returns:
 *   boolean: True if sidebar should be expanded, false for mini mode.
 *            Defaults to true if no saved state exists.
 */
export function getSavedSidebarState(): boolean {
  const saved = safeGetItem(STORAGE_KEYS.SIDEBAR_EXPANDED);
  // Default to expanded if no saved state
  if (saved === null) {
    return true;
  }
  return saved === "true";
}

/**
 * Saves the sidebar expansion state to localStorage.
 *
 * Args:
 *   isExpanded: Whether the sidebar should be expanded.
 */
export function saveSidebarState(isExpanded: boolean): void {
  safeSetItem(STORAGE_KEYS.SIDEBAR_EXPANDED, String(isExpanded));
}

/**
 * Initializes the saved sidebar state if not already set.
 * Sets default to "true" (expanded).
 */
export function initializeSidebarState(): void {
  if (safeGetItem(STORAGE_KEYS.SIDEBAR_EXPANDED) === null) {
    safeSetItem(STORAGE_KEYS.SIDEBAR_EXPANDED, "true");
  }
}

// ============================================
// Dropdown State Persistence
// ============================================

/**
 * Gets the saved dropdown states from localStorage.
 *
 * Returns:
 *   Record<string, boolean>: Object mapping dropdown IDs to open/close state.
 *                            Returns empty object if no saved state or invalid JSON.
 */
export function getDropdownStates(): Record<string, boolean> {
  const stored = safeGetItem(STORAGE_KEYS.DROPDOWN_STATES);
  if (!stored) {
    return {};
  }
  try {
    return JSON.parse(stored) as Record<string, boolean>;
  } catch (error) {
    console.debug("[sidebar] Failed to parse dropdown states JSON:", error);
    return {};
  }
}

/**
 * Saves a single dropdown's open/close state to localStorage.
 *
 * Merges with existing dropdown states rather than replacing.
 *
 * Args:
 *   dropdownId: The ID of the dropdown (e.g., "dropdown-samai").
 *   isOpen: Whether the dropdown is open.
 */
export function saveDropdownState(dropdownId: string, isOpen: boolean): void {
  const states = getDropdownStates();
  states[dropdownId] = isOpen;
  safeSetItem(STORAGE_KEYS.DROPDOWN_STATES, JSON.stringify(states));
}

/**
 * Clears all saved dropdown states from localStorage.
 */
export function clearDropdownStates(): void {
  safeRemoveItem(STORAGE_KEYS.DROPDOWN_STATES);
}

/**
 * Gets the open/close state for a specific dropdown.
 *
 * Args:
 *   dropdownId: The ID of the dropdown.
 *
 * Returns:
 *   boolean | undefined: The saved state, or undefined if not saved.
 */
export function getDropdownState(dropdownId: string): boolean | undefined {
  const states = getDropdownStates();
  return states[dropdownId];
}

// ============================================
// DOM State Helpers
// ============================================

/**
 * Checks if a dropdown element is currently visible (not hidden).
 *
 * Args:
 *   dropdownEl: The dropdown element.
 *
 * Returns:
 *   boolean: True if dropdown is visible (no "hidden" class).
 */
export function isDropdownOpen(dropdownEl: HTMLElement | null): boolean {
  if (!dropdownEl) return false;
  return !dropdownEl.classList.contains("hidden");
}

/**
 * Gets the aria-expanded state from a toggle button.
 *
 * Args:
 *   buttonEl: The button element with data-collapse-toggle.
 *
 * Returns:
 *   boolean: True if aria-expanded is "true".
 */
export function getAriaExpanded(buttonEl: HTMLElement | null): boolean {
  if (!buttonEl) return false;
  return buttonEl.getAttribute("aria-expanded") === "true";
}

/**
 * Sets the aria-expanded state on a toggle button.
 *
 * Args:
 *   buttonEl: The button element.
 *   isExpanded: The expanded state to set.
 */
export function setAriaExpanded(buttonEl: HTMLElement, isExpanded: boolean): void {
  buttonEl.setAttribute("aria-expanded", String(isExpanded));
}
